import { Button, Grid2, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  OrganizationState,
  useOrganizationState,
} from 'features/organizations/state/useOrganizationState';
import { useOrganizationPricing } from 'features/organizations/hooks/useOrganizationPricing';
import { useForm } from 'react-hook-form';
import { useOrganizationPurchase } from 'features/organizations/hooks/useOrganizationPurchase';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { PurchaseFlowStep } from 'features/organizations/organizations.types';
import { useAuthState } from 'state/useAuthState';
import { yupResolver } from '@hookform/resolvers/yup';
import { getOrgCommonSignupFormsValidationSchema } from 'features/organizations/organizations.utils';
import { OrganizationSeatsFormFields } from '../OrganizationSeatsFormFields';
import { OrganizationPurchaseSummary } from '../OrganizationPurchaseSummary';

type OrganizationSeatsFormValues = {
  regularSeats: number;
  proSeats: number;
};

type OrganizationSeatsFormProps = {
  onSubmit: (
    values: Partial<OrganizationState>,
    nextStep?: PurchaseFlowStep,
  ) => void;
};

export const OrganizationSeatsForm = (
  props: OrganizationSeatsFormProps,
): JSX.Element => {
  const { onSubmit } = props;
  const { t } = useTranslation();
  const { proSeats: initialProSeats, regularSeats: initialRegularSeats } =
    useOrganizationState();
  const { pricing, isLoading, isError } = useOrganizationPricing();
  const { user } = useAuthState();

  const { control, handleSubmit, formState, watch } =
    useForm<OrganizationSeatsFormValues>({
      resolver: yupResolver(
        getOrgCommonSignupFormsValidationSchema({
          t,
          existingUser: Boolean(user),
          emailValidationCache: new Map<string, boolean>(),
          fields: ['regularSeats', 'proSeats'],
        }),
      ),
      defaultValues: {
        regularSeats: initialRegularSeats || 0,
        proSeats: initialProSeats || 0,
      },
      mode: 'onBlur',
    });

  const regularSeats = watch('regularSeats');
  const proSeats = watch('proSeats');

  const { isLoading: isSummaryLoading, isError: isSummaryError } =
    useOrganizationPurchase({
      processOrganizationPurchase: { regularSeats, proSeats },
    });

  const handleNext = (values: OrganizationSeatsFormValues): void => {
    onSubmit(values);
  };

  const handleQuote = (values: OrganizationSeatsFormValues): void => {
    onSubmit(values, 'quote');
  };

  if (isLoading || isError) {
    return <LoadingAndErrors isLoading={isLoading} />;
  }

  const pendingSummary = isSummaryLoading || isSummaryError;
  const isDisabled = !pricing || pendingSummary || !formState.isValid;

  return (
    <Grid2
      container
      direction="column"
      spacing={2}
      sx={{ flex: 1, pt: 1 }}
      component="form"
      noValidate
      onSubmit={handleSubmit(handleNext)}
    >
      <Grid2 container direction="column" spacing={2}>
        <Grid2>
          <Typography variant="h2">{t('organization.featureName')}</Typography>
        </Grid2>
        <OrganizationSeatsFormFields
          control={control}
          formState={formState}
          isDisabled={formState.isSubmitting}
          pricing={pricing}
          showLearnMore
        />
        <OrganizationPurchaseSummary
          regularSeats={regularSeats}
          proSeats={proSeats}
        />
      </Grid2>
      <Grid2
        size="grow"
        container
        direction="column"
        sx={{ justifyContent: 'flex-end' }}
      >
        <Stack spacing={2}>
          <Button
            size="large"
            fullWidth
            variant="outlined"
            onClick={handleSubmit(handleQuote)}
            disabled={isDisabled}
          >
            {t('organizationPurchase.sendAndEmailQuote')}
          </Button>
          <Button size="large" fullWidth type="submit" disabled={isDisabled}>
            {t('organizationPurchase.checkout')}
          </Button>
        </Stack>
      </Grid2>
    </Grid2>
  );
};
